import React, { useCallback } from 'react';

import { GridCellWithAvatar } from '@vyce/core/src/components';
import { getTime } from '@vyce/core/src/utils/dates';
import { getAvatar } from '@vyce/core/src/utils';
import { WidgetWrapperProps } from '@vyce/core/src/components/Dashboard/WidgetWrapper';

import { WidgetTemplate } from './WidgetTemplate';
import { Props } from './types';
import { EnrichedColumnProps } from '../../../components/InfiniteTable/types';
import { CheckOutWorkerDTO } from '../../../api/types';
import useChatRequests from '../../../hooks/useChatRequests';
import { TimeLogWarningCell } from '../../../components/EditWorkerHoursDialog/components/TimeLogWarningCell';
import { useSchedule } from '../../../hooks/useSchedule';

export const ClockOutWidget = (props: Props & WidgetWrapperProps) => {
  const { createPrivateChat } = useChatRequests();
  const { isClockOutEarly, getShiftSchedule } = useSchedule();

  const goToChat = async (worker: CheckOutWorkerDTO) => {
    if (!props.chatUser || !props.selectedCompanyId) {
      return;
    }
    await createPrivateChat({
      creator: props.chatUser,
      chatUser: {
        uid: worker.user_id,
        name: `${worker.first_name} ${worker.last_name}`,
        email: worker.email,
        phone: worker.phone,
        avatarUrl: worker.photo_url,
      },
      companyId: props.selectedCompanyId,
    });
  };

  const getColumns = useCallback(
    (cnaViewWorker: boolean): EnrichedColumnProps<CheckOutWorkerDTO>[] => [
      {
        field: 'first_name',
        headerName: 'Name',
        flex: 1,
        width: 150,
        sortable: true,
        renderCell: row => (
          <GridCellWithAvatar
            avatarUrl={row.photo_url}
            avatarPlaceholder={getAvatar(row.gender)}
            link={cnaViewWorker ? `/time/users/${row.first_name}_${row.user_id}/logs` : ''}
            name={`${row.first_name} ${row.last_name}`}
            target="_blank"
            createPrivateChat={() => goToChat(row)}
          />
        ),
      },
      {
        field: 'role',
        headerName: 'Role',
        flex: 0.8,
        width: 100,
        sortable: true,
      },
      {
        field: 'site_name',
        headerName: 'Location',
        flex: 0.8,
        width: 100,
        sortable: true,
      },

      {
        field: 'checked_out',
        headerName: 'Clocked out',
        flex: 0.8,
        width: 100,
        sortable: true,
        renderCell: row => (
          <TimeLogWarningCell
            check_in_ok={true}
            face_in_ok={true}
            minutesEarly={isClockOutEarly(
              getShiftSchedule(row.shift_id, props.locations) || [],
              new Date(row.checked_out)
            )}>
            <>{getTime(row.checked_out)}</>
          </TimeLogWarningCell>
        ),
      },
    ],
    []
  );

  return <WidgetTemplate getColumns={getColumns} {...props} type="clock_out" />;
};
